import { getOrderContentColumnScrollEl, isOrderContentColumnAtScrollRest } from "./orderMealsScrollChaining";

export type OrderMealsLayoutSyncOptions = {
    /** When true, update CSS vars even if `.content-column` is scrolled (resize, day change, scroll back to top). */
    force?: boolean;
};

const MEALS_VIEWPORT_TOP_EPSILON_PX = 2;

/** Meal-list cap uses this while `.content-column` is scrolled — avoids growing `.order-body-scroll` and a gap above the amount row. */
let mealsListViewportTopAtColumnScrollRest: number | null = null;

export function resetMealsListViewportTopAtColumnScrollRest(): void {
    mealsListViewportTopAtColumnScrollRest = null;
}

/** Keeps catalog `top` in sync with measured day-strip height (wide sticky order layout). */
export function syncOrderStickyChromeCssVars(options: OrderMealsLayoutSyncOptions = {}): void {
    if (!options.force && !isOrderContentColumnAtScrollRest()) {
        return;
    }

    const region = document.querySelector<HTMLElement>(".order-scroll-sticky-region");
    const banner = region?.querySelector<HTMLElement>(".order-info-banner-section");
    const dayChrome = region?.querySelector<HTMLElement>(".order-day-chrome-sticky");
    const day = dayChrome?.querySelector<HTMLElement>(".day-selector")
        ?? document.querySelector<HTMLElement>(".order-scroll-sticky-region > .day-selector");
    const catalog = document.querySelector<HTMLElement>(".order-scroll-sticky-region > .order-catalog-heading");
    const amount = region?.querySelector<HTMLElement>(".order-amount-block");
    const actions = region?.querySelector<HTMLElement>(".order-sticky-actions-bar");
    if (banner) {
        document.documentElement.style.setProperty("--order-info-banner-height", `${banner.offsetHeight}px`);
    } else {
        document.documentElement.style.removeProperty("--order-info-banner-height");
    }
    if (dayChrome) {
        document.documentElement.style.setProperty("--order-day-chrome-sticky-height", `${dayChrome.offsetHeight}px`);
    } else {
        document.documentElement.style.removeProperty("--order-day-chrome-sticky-height");
    }
    if (day) {
        document.documentElement.style.setProperty("--order-day-selector-sticky-height", `${day.offsetHeight}px`);
    } else {
        document.documentElement.style.removeProperty("--order-day-selector-sticky-height");
    }
    if (catalog) {
        document.documentElement.style.setProperty("--order-catalog-sticky-height", `${catalog.offsetHeight}px`);
    } else {
        document.documentElement.style.removeProperty("--order-catalog-sticky-height");
    }
    if (amount) {
        document.documentElement.style.setProperty("--order-amount-block-height", `${amount.offsetHeight}px`);
    } else {
        document.documentElement.style.setProperty("--order-amount-block-height", "0px");
    }
    if (actions) {
        document.documentElement.style.setProperty("--order-sticky-actions-height", `${actions.offsetHeight}px`);
    } else {
        document.documentElement.style.setProperty("--order-sticky-actions-height", "0px");
    }
    const column = getOrderContentColumnScrollEl();
    if (column) {
        document.documentElement.style.setProperty("--order-content-column-visible-height", `${column.clientHeight}px`);
    } else {
        document.documentElement.style.removeProperty("--order-content-column-visible-height");
    }
}

function measureMealsListViewportTopFromRegionStack(
    region: HTMLElement,
    bodyScroll: HTMLElement,
): number | null {
    const column = getOrderContentColumnScrollEl();
    const content = column?.querySelector<HTMLElement>(".content");
    if (!column || !content) {
        return null;
    }

    const contentTop = content.getBoundingClientRect().top;
    const padTop = parseFloat(getComputedStyle(content).paddingTop) || 0;
    let stackTop = contentTop + padTop;
    for (const child of region.children) {
        if (child === bodyScroll) {
            break;
        }
        if (child instanceof HTMLElement && !child.classList.contains("only-print")) {
            stackTop += child.offsetHeight;
        }
    }
    return Math.round(stackTop);
}

function readMealsListViewportTopCssPx(): number | null {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--order-meals-list-viewport-top");
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

/** Top of `.order-body-scroll` in viewport px for meal-list max-height (does not shrink when column scrolls — prevents a gap above the amount row). */
export function syncOrderMealsListViewportTopCssVar(options: OrderMealsLayoutSyncOptions = {}): void {
    const bodyScroll = document.querySelector<HTMLElement>(
        "#orderApp .order-scroll-sticky-region--has-actions:not(.order-scroll-sticky-region--no-meals) .order-body-scroll:not(.order-body-scroll--compact)",
    );
    if (!bodyScroll) {
        document.documentElement.style.removeProperty("--order-meals-list-viewport-top");
        return;
    }

    const region = bodyScroll.closest<HTMLElement>(".order-scroll-sticky-region");
    const liveTop = Math.round(bodyScroll.getBoundingClientRect().top);
    const stackTop = region ? measureMealsListViewportTopFromRegionStack(region, bodyScroll) : null;
    const atColumnScrollRest = isOrderContentColumnAtScrollRest();

    const prevTop = readMealsListViewportTopCssPx();

    let nextTop: number;
    if (atColumnScrollRest) {
        nextTop = stackTop ?? liveTop;
        mealsListViewportTopAtColumnScrollRest = nextTop;
    } else {
        const baseline = mealsListViewportTopAtColumnScrollRest ?? prevTop ?? stackTop ?? liveTop;
        nextTop = Math.max(baseline, liveTop);
        // Smaller viewport-top → taller meal cap; do not shrink top while the column is scrolled.
        if (prevTop != null) {
            nextTop = Math.max(nextTop, prevTop);
        }
    }
    if (
        !options.force &&
        prevTop != null &&
        Math.abs(nextTop - prevTop) < MEALS_VIEWPORT_TOP_EPSILON_PX
    ) {
        return;
    }

    document.documentElement.style.setProperty("--order-meals-list-viewport-top", `${nextTop}px`);
}

/** Subtle shadow when the day strip is pinned to the content column (wide layout). */
export function bindOrderDaySelectorStuckState(daySelector: HTMLElement): () => void {
    const column = getOrderContentColumnScrollEl();
    if (!column) {
        return () => undefined;
    }

    const dayChrome = daySelector.closest<HTMLElement>(".order-day-chrome-sticky");

    let frame = 0;
    const update = (): void => {
        const content = column.querySelector<HTMLElement>(".content");
        const contentTop = content?.getBoundingClientRect().top ?? column.getBoundingClientRect().top;
        const padTop = content ? parseFloat(getComputedStyle(content).paddingTop) || 0 : 0;
        const pinTop = contentTop + padTop;
        const dayTop = daySelector.getBoundingClientRect().top;
        const stuck = dayTop <= pinTop + 1;
        daySelector.classList.toggle("day-selector--stuck", stuck);
        dayChrome?.classList.toggle("order-day-chrome-sticky--pinned", stuck);
    };

    const onScroll = (): void => {
        if (frame) {
            cancelAnimationFrame(frame);
        }
        frame = requestAnimationFrame(update);
    };

    column.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();

    return () => {
        column.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        if (frame) {
            cancelAnimationFrame(frame);
        }
        daySelector.classList.remove("day-selector--stuck");
        dayChrome?.classList.remove("order-day-chrome-sticky--pinned");
    };
}
